import React, { useState } from "react";
import { Currency, experiences, PAYPAL_HANDLE } from "./wishingWellData";
import styles from "./WishingWell.module.css";

interface WishingWellProps {
  currency: Currency;
  onCurrencyChange: (currency: Currency) => void;
}

// Progressive Amount Slider Component
const ProgressiveSlider: React.FC<{ currency: Currency }> = ({ currency }) => {
  const [selectedIndex, setSelectedIndex] = useState(2); // Default to middle option
  const [customAmount, setCustomAmount] = useState("");
  
  const makeLink = (amt: number) =>
    `https://paypal.me/${PAYPAL_HANDLE}/${amt}${currency}`;

  const currentExperience = experiences[selectedIndex];
  const isCustomAmount = selectedIndex === experiences.length - 1; // Last option is custom
  const amount = currency === "USD" ? currentExperience.amountUSD : currentExperience.amountAUD;
  const symbol = currency === "USD" ? "$" : "A$";
  
  const clean = (v: string) => {
    // Remove all non-numeric characters except decimal point
    const numericOnly = v.replace(/[^\d.]/g, "");
    
    // Handle multiple decimal points - keep only the first one
    const parts = numericOnly.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    return numericOnly;
  };
  
  const formatAmount = (value: string) => {
    if (!value || value === '') return '';
    
    // If the value doesn't contain a decimal point, add .00
    if (!value.includes('.')) {
      return value + '.00';
    }
    
    // If it has a decimal but no digits after, add 00
    if (value.endsWith('.')) {
      return value + '00';
    }
    
    // If it has one digit after decimal, add one 0
    const parts = value.split('.');
    if (parts[1] && parts[1].length === 1) {
      return value + '0';
    }
    
    // If it has exactly 2 decimal places, keep it as is
    if (parts[1] && parts[1].length === 2) {
      return value;
    }
    
    // If it has more than 2 decimal places, truncate to 2
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    return value;
  };
  
  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = clean(e.target.value);
    setCustomAmount(cleanedValue);
  };
  
  const handleCustomAmountBlur = () => {
    if (customAmount) {
      setCustomAmount(formatAmount(customAmount));
    }
  };
  
  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (Number(customAmount) > 0) {
      const href = `https://paypal.me/${PAYPAL_HANDLE}/${Number(customAmount)}${currency}`;
      window.open(href, "_blank", "noopener");
    }
  };

  return (
    <div className={styles.progressiveSlider}>
      <div className={styles.sliderContainer}>
        <h3 className={styles.experienceTitle}>{currentExperience.label}</h3>
        <input
          type="range"
          min="0"
          max={experiences.length - 1}
          value={selectedIndex}
          onChange={(e) => setSelectedIndex(parseInt(e.target.value))}
          className={styles.slider}
          style={{
            '--fill-percent': `${(selectedIndex / (experiences.length - 1)) * 100}%`
          } as React.CSSProperties}
        />
        <div className={styles.sliderLabels}>
          {experiences.map((exp, index) => (
            <div
              key={index}
              className={`${styles.sliderLabel} ${index === selectedIndex ? styles.active : ''}`}
              onClick={() => setSelectedIndex(index)}
            >
              <span className={styles.labelIcon}>{exp.icon}</span>
              <span className={styles.labelAmount}>
                {index === experiences.length - 1 ? "Custom" : `${symbol}${currency === "USD" ? exp.amountUSD : exp.amountAUD}`}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {!isCustomAmount && (
        <a
          href={makeLink(amount)}
          target="_blank"
          rel="noopener noreferrer"
          className="accent-btn"
        >
          Contribute ${amount}
        </a>
      )}
      
      {isCustomAmount && (
        <div className={styles.customAmountContainer}>
          <form onSubmit={handleCustomSubmit} className={styles.sliderCustomForm}>
            <div className={styles.sliderCustomInputGroup}>
              <span className={styles.sliderCurrencySymbol}>$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={customAmount}
                onChange={handleCustomAmountChange}
                onBlur={handleCustomAmountBlur}
                placeholder="100.00"
                className={styles.sliderCustomInput}
                autoFocus
              />
              <button 
                type="submit" 
                disabled={!Number(customAmount)}
                className="accent-btn"
              >
                Contribute
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const WishingWell: React.FC<WishingWellProps> = ({ currency, onCurrencyChange }) => {
  return (
    <div className="card">
      <div className="cardContent">
        <div className={styles.wishingWellContent}>          
          <ProgressiveSlider currency={currency} />
          
          <div className={styles.currencyToggle}>
            <div className={styles.currencyInstructions}>
              Toggle the currency to make payment in the relevant currency
            </div>
            <div className={styles.currencySelector}>
              <button 
                onClick={() => onCurrencyChange("USD")}
                className={`${styles.currencyOption} ${currency === "USD" ? styles.active : ""}`}
              >
                <span className={styles.flagIcon}>🇺🇸</span>
                <span className={styles.currencyCode}>USD</span>
              </button>
              <span className={styles.currencySeparator}>|</span>
              <button 
                onClick={() => onCurrencyChange("AUD")}
                className={`${styles.currencyOption} ${currency === "AUD" ? styles.active : ""}`}
              >
                <span className={styles.flagIcon}>🇦🇺</span>
                <span className={styles.currencyCode}>AUD</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WishingWell;
